import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faChevronLeft, faChevronRight, faMobileAlt, faClock, faCheckCircle, faSpinner, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "../../UI/Layout/AdminLayout";
import Title from "../../UI/Text/Title";
import Alert from "../../UI/Alert/Alert";
import axios from "../../../axios/Axios";
import { useParams } from "react-router-dom";
import { HOST_URL, NODE_HOST_URL_v1 } from "../api/Api";
import { userloginDetails } from "../../Utils/AdminAuth";
import Axios from "../../../axios/Axios";

const STATUS_CONFIG = {
  delivered: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  failed: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  read: { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  sent: { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  default: { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
};

const getStatusStyle = (status) =>
  STATUS_CONFIG[status ? status.toLowerCase() : ""] || STATUS_CONFIG.default;

const CampaignReportWhatsApp = () => {
  const lStorage = userloginDetails();
  const userId = lStorage && lStorage.user ? lStorage.user.userId : null;
  const userToken = lStorage ? lStorage.token : null;

  const { camp_user_id, camp_id, camp_user_email, totalData } = useParams();
  const totalDataCount = parseInt(totalData, 10) || 0;
  const dialogRef = useRef(null);
  const perPageRef = useRef(10);

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiLogData, setApiLogData] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(totalDataCount);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchInput,   setSearchInput]   = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [windowWidth,   setWindowWidth]   = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  const fetchData = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("method", "view_report_admin");
      formData.append("token", userToken);
      formData.append("channel", "whatsapp");
      formData.append("camp_user_id", camp_user_id);
      formData.append("camp_id", camp_id);
      formData.append("camp_user_email", camp_user_email);
      formData.append("page_no", page);

      const res = await axios.post(`${HOST_URL}/bulk_campaign_sms.php`, formData);

      if (res.data.success === true) {
        const records = Array.isArray(res.data.data) ? res.data.data : [];
        if (page === 1 && records.length > 0) {
          perPageRef.current = records.length;
          setPerPage(records.length);
        }
        const pp = perPageRef.current;
        const pages = totalDataCount > 0 && pp > 0 ? Math.ceil(totalDataCount / pp) : 1;
        setReportData(records);
        setTotalRecords(totalDataCount);
        setTotalPages(Math.max(1, pages));
        setCurrentPage(page);
      } else {
        setError(res.data.message || "Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, camp_user_id, camp_id, camp_user_email, totalDataCount]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  useEffect(() => {
    const handler = (e) => {
      setApiLogData(e.detail);
      if (dialogRef.current) dialogRef.current.showModal();
    };
    window.addEventListener("openApiLogModal", handler);
    return () => window.removeEventListener("openApiLogModal", handler);
  }, []);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchData(page);
  };

  const renderPagePills = () => {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, []);

    return pages.map((p, idx) =>
      p === "..." ? (
        <span key={`e-${idx}`} style={{ padding: "0 6px", color: "#94a3b8", fontSize: "13px" }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          style={{
            minWidth: "34px", height: "34px",
            border: "1px solid",
            borderColor: currentPage === p ? "#4f46e5" : "#e2e8f0",
            background: currentPage === p ? "#4f46e5" : "#fff",
            color: currentPage === p ? "#fff" : "#475569",
            borderRadius: "8px", fontSize: "13px",
            fontWeight: currentPage === p ? "700" : "400",
            cursor: "pointer",
          }}
        >
          {p}
        </button>
      )
    );
  };

  const SearchNumber = async () => {
    const query = searchInput.trim();
    if (!query) {
      setSearchQuery("");
      setSearchResults([]);
      return;
    }

    setError(null);
    setSearching(true);
    try {
      const paramData = {
        user_id: userId,
        method: "NumberSearch",
        number: parseInt(searchInput),
        campaign_id: parseInt(camp_id),
        username: camp_user_email,
        filter: "wpSearch",
        token: userToken,
      };
      const { data } = await axios.post(`${NODE_HOST_URL_v1}/admin/CampaignSearch`, paramData);
      if (data.success) {
        setSearchResults(Array.isArray(data.data) ? data.data : []);
        setSearchQuery(query);
      } else {
        setSearchResults([]);
        setSearchQuery(query);
        setError(data.message || "Search failed. Please try again.");
      }
    } catch (err) {
      setSearchResults([]);
      setError(err.response?.data?.message || "Network error during search.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <AdminLayout>
      <main className="content">
        <div className="container-fluid p-0">
          <Title isButton={false} btnName="Create New Contact List" actionURL="/dashboard/create-contact-list">Whatsapp Campaign Reports</Title>

          {error && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "12px", padding: isMobile ? "12px 14px" : "14px 18px",
              marginBottom: "16px",
            }}>
              <span style={{ fontSize: "18px", lineHeight: 1 }}>&#9888;</span>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#dc2626", marginBottom: "2px" }}>{error}</div>
                {/* <div style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div> */}
              </div>
              <button
                onClick={() => setError(null)}
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#f87171", fontSize: "16px", lineHeight: 1, padding: 0 }}
              >
                &#x2715;
              </button>
            </div>
          )}

          <div style={{
            background: "#fff", borderRadius: isMobile ? "12px" : "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            fontFamily: "Inter, system-ui, sans-serif",
            overflow: "hidden",
          }}>
            {/* ── Header ── */}
            <div style={{
              padding: isMobile ? "14px 16px" : "20px 24px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? "12px" : "0",
            }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? "15px" : "18px", fontWeight: "800", color: "#0f172a" }}>
                Campaign ID: <span style={{ color: "#4f46e5" }}>{camp_id}</span>
              </h3>

              {totalDataCount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: isMobile ? "100%" : "auto" }}>
                  <div style={{ position: "relative", flex: 1 }}>
                    <FontAwesomeIcon
                      icon={faSearch}
                      style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px", pointerEvents: "none" }}
                    />
                    <input
                      type="text"
                      placeholder="Search mobile number..."
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") SearchNumber(); }}
                      style={{
                        width: "100%",
                        paddingLeft: "30px", paddingRight: searchInput ? "30px" : "12px",
                        height: "36px", border: "1px solid #e2e8f0", borderRadius: "8px",
                        fontSize: "13px", color: "#334155", outline: "none",
                        background: "#f8fafc", boxSizing: "border-box",
                      }}
                    />
                    {searchInput && (
                      <button
                        onClick={() => { setSearchInput(""); setSearchQuery(""); setSearchResults([]); }}
                        style={{
                          position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)",
                          background: "none", border: "none", cursor: "pointer", padding: 0,
                          color: "#94a3b8", fontSize: "11px", lineHeight: 1,
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={SearchNumber}
                    style={{
                      height: "36px", padding: "0 14px",
                      background: "#4f46e5", color: "#fff",
                      border: "none", borderRadius: "8px",
                      fontSize: "13px", fontWeight: "600", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: "6px",
                      whiteSpace: "nowrap", flexShrink: 0,
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} style={{ fontSize: "11px" }} />
                    {!isMobile && "Search"}
                  </button>
                </div>
              )}
            </div>

            {/* ── Table ── */}
            {loading || searching ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: isMobile ? "48px 0" : "80px 0", flexDirection: "column", gap: "12px" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "28px", color: "#4f46e5" }} />
                <span style={{ color: "#64748b", fontSize: "13px" }}>{searching ? "Searching…" : "Loading records…"}</span>
              </div>
            ) : (searchQuery ? searchResults : reportData).length === 0 ? (
              <div style={{ textAlign: "center", padding: isMobile ? "48px 16px" : "80px 0", color: "#94a3b8" }}>
                <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: "14px" }}>
                  {searchQuery ? "No records found for this search." : "No records found for this campaign."}
                </p>
              </div>
            ) : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "780px" }}>
                  <thead style={{ background: "#f8fafc" }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      {["Mobile Number", "Sent Time", "Delivered Time", "Status", "DLR Response", "Parameters"].map((h, i) => (
                        <th key={i} style={{
                          padding: "12px 16px", fontSize: "11px", fontWeight: "700",
                          color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
                          textAlign: "left", whiteSpace: "nowrap",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(searchQuery ? searchResults : reportData).map((row, ri) => {
                      const sts = getStatusStyle(row.status);
                      return (
                        <tr
                          key={ri}
                          style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          {/* Mobile */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{row.mobile}</span>
                            </div>
                          </td>

                          {/* Sent Time */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
                              <span style={{ fontSize: "12px", color: "#475569" }}>{row.created_date || "—"}</span>
                            </div>
                          </td>

                          {/* Delivered Time */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <FontAwesomeIcon icon={row.deliver_time ? faCheckCircle : faClock} style={{ color: row.deliver_time ? "#16a34a" : "#cbd5e1", fontSize: "11px" }} />
                              <span style={{ fontSize: "12px", color: "#475569" }}>{row.deliver_time || "—"}</span>
                            </div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span style={{
                                background: sts.bg, color: sts.color, border: `1px solid ${sts.border}`,
                                padding: "3px 10px", borderRadius: "100px", fontSize: "11px", fontWeight: "700",
                                textTransform: "capitalize", whiteSpace: "nowrap",
                              }}>
                                {row.status}
                              </span>
                              {row.api_status && (
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  style={{ cursor: "pointer", fontSize: "13px", color: "#94a3b8" }}
                                  title="View API Log"
                                  onClick={() => window.dispatchEvent(new CustomEvent("openApiLogModal", { detail: row.api_status }))}
                                />
                              )}
                            </div>
                          </td>

                          {/* DLR Response */}
                          <td style={{ padding: "14px 16px", width: "200px", maxWidth: "200px" }}>
                            {(() => {
                              const dlrText = row.status === "failed" && !row.dlr_reponse
                                ? (() => {
                                  try {
                                    const parsed = typeof row.api_log_id === "string" ? JSON.parse(row.api_log_id) : row.api_log_id;
                                    return (parsed && parsed.error && parsed.error.message) ? parsed.error.message : "Unknown error";
                                  } catch (e) {
                                    return "—";
                                  }
                                })()
                                : row.dlr_reponse || "—";
                              return (
                                <div style={{ position: "relative" }}
                                  onMouseEnter={e => {
                                    const tip = e.currentTarget.querySelector(".dlr-tip");
                                    if (tip) tip.style.display = "block";
                                  }}
                                  onMouseLeave={e => {
                                    const tip = e.currentTarget.querySelector(".dlr-tip");
                                    if (tip) tip.style.display = "none";
                                  }}
                                >
                                  <span style={{
                                    fontSize: "12px", color: "#475569",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                    cursor: dlrText !== "—" ? "pointer" : "default",
                                  }}>
                                    {dlrText}
                                  </span>
                                  {dlrText !== "—" && (
                                    <div className="dlr-tip" style={{
                                      display: "none",
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      zIndex: 999,
                                      background: "#1e293b",
                                      color: "#f1f5f9",
                                      fontSize: "12px",
                                      padding: "10px 12px",
                                      borderRadius: "8px",
                                      maxWidth: "320px",
                                      minWidth: "180px",
                                      wordBreak: "break-word",
                                      whiteSpace: "pre-wrap",
                                      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                                      lineHeight: "1.6",
                                      marginTop: "4px",
                                    }}>
                                      {dlrText}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Parameters */}
                          <td style={{ padding: "14px 16px", maxWidth: "200px" }}>
                            {row.parameter ? (
                              <pre style={{
                                margin: 0, fontSize: "11px", color: "#475569",
                                background: "#f8fafc", border: "1px solid #e2e8f0",
                                borderRadius: "6px", padding: "6px 8px",
                                maxHeight: "80px", overflowY: "auto",
                                whiteSpace: "pre-wrap", wordBreak: "break-all",
                              }}>
                                {typeof row.parameter === "object"
                                  ? JSON.stringify(row.parameter, null, 2)
                                  : row.parameter}
                              </pre>
                            ) : (
                              <span style={{ fontSize: "12px", color: "#cbd5e1" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Pagination ── */}
            {!loading && reportData.length > 0 && !searchQuery && (
              <div style={{
                padding: isMobile ? "12px 16px" : "16px 24px",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: "10px",
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {totalRecords > 0 ? (
                    <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#334155", fontWeight: "600" }}>
                      Showing{" "}
                      <span style={{ color: "#4f46e5" }}>{(currentPage - 1) * perPage + 1}</span>
                      {" – "}
                      <span style={{ color: "#4f46e5" }}>{Math.min(currentPage * perPage, totalRecords)}</span>
                      {" of "}
                      <span style={{ color: "#4f46e5" }}>{totalRecords}</span>
                      {" records"}
                      {!isMobile && (
                        <span style={{ color: "#94a3b8", fontWeight: "400", fontSize: "12px", marginLeft: "6px" }}>
                          (Page {currentPage} of {totalPages})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span style={{ fontSize: "13px", color: "#64748b" }}>
                      Page <b style={{ color: "#4f46e5" }}>{currentPage}</b>
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: isMobile ? "center" : "flex-end", flexWrap: "wrap" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      height: isMobile ? "32px" : "34px", padding: "0 12px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage === 1 ? "#f8fafc" : "#fff",
                      color: currentPage === 1 ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "10px" }} /> Prev
                  </button>

                  {!isMobile && renderPagePills()}

                  {isMobile && (
                    <span style={{ fontSize: "13px", color: "#64748b", padding: "0 8px" }}>
                      {currentPage} / {totalPages}
                    </span>
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                      height: isMobile ? "32px" : "34px", padding: "0 12px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage >= totalPages ? "#f8fafc" : "#fff",
                      color: currentPage >= totalPages ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "10px" }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── API Log Modal ── */}
        <dialog
          ref={dialogRef}
          style={{
            padding: "24px", width: "min(800px, 95vw)",
            borderRadius: "12px", border: "1px solid #e2e8f0",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            fontFamily: "Inter, system-ui, sans-serif",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>API Status Log</h5>
            <button
              onClick={() => { if (dialogRef.current) dialogRef.current.close(); }}
              style={{
                background: "#f1f5f9", border: "none", borderRadius: "50%",
                width: "32px", height: "32px", cursor: "pointer",
                fontSize: "16px", color: "#64748b", display: "flex",
                alignItems: "center", justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
          <pre style={{
            fontSize: "12px", background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: "8px", padding: "14px", overflowX: "auto",
            maxHeight: "400px", overflowY: "auto", margin: 0, color: "#334155",
          }}>
            {apiLogData}
          </pre>
        </dialog>
      </main>
    </AdminLayout>
  );
};

export default CampaignReportWhatsApp;
